import { createSlice } from '@reduxjs/toolkit';

// Функция для получения уникальных значений из массива
const getUniqueValues = (arr) => {
  return [...new Set(arr.filter((item) => item !== null && item !== undefined && item !== ''))];
};

// Функция для фильтрации публикаций по трем параметрам (включая поиск) и по конкретному посту
const filterPublications = (
  publications,
  filterAValue,
  filterBValue,
  searchValue = '',
  selectedPostFilter = null,
) => {
  if (!publications || !Array.isArray(publications)) {
    return [];
  }

  const result = publications.filter((publication) => {
    // Если установлен фильтр по конкретному посту, показываем только его
    if (selectedPostFilter) {
      return publication.eng_name === selectedPostFilter;
    }

    // Парсим publication_settings для получения данных фильтрации
    let publicationSettings = {};
    try {
      if (
        publication.publication_settings &&
        typeof publication.publication_settings === 'string'
      ) {
        // Проверяем, является ли строка валидным JSON
        if (
          publication.publication_settings.startsWith('{') ||
          publication.publication_settings.startsWith('[')
        ) {
          publicationSettings = JSON.parse(publication.publication_settings);
        }
      }
    } catch (error) {
      // Если не удалось распарсить, оставляем пустой объект
    }

    // Фильтр по filter_a_value
    let matchesFilterA = true;
    if (filterAValue && filterAValue !== 'все') {
      matchesFilterA =
        publicationSettings.publication_filter_a &&
        publicationSettings.publication_filter_a.includes(filterAValue);
    }

    // Фильтр по filter_b_value
    let matchesFilterB = true;
    if (filterBValue && filterBValue !== 'все') {
      matchesFilterB =
        publicationSettings.publication_filter_b &&
        publicationSettings.publication_filter_b.includes(filterBValue);
    }

    // Фильтр по поисковому запросу
    let matchesSearch = true;
    if (searchValue && searchValue.trim() !== '') {
      const searchTerm = searchValue.toLowerCase().trim();

      // Поиск по publication_search_words (строка, разделенная запятыми)
      let searchWordsFromSettings = [];
      if (publicationSettings.publication_search_words) {
        // Если это строка, разделяем по запятым и убираем пробелы
        if (typeof publicationSettings.publication_search_words === 'string') {
          searchWordsFromSettings = publicationSettings.publication_search_words
            .split(',')
            .map((word) => word.trim().toLowerCase());
        }
        // Если это массив, используем как есть
        else if (Array.isArray(publicationSettings.publication_search_words)) {
          searchWordsFromSettings = publicationSettings.publication_search_words.map((word) =>
            word.toLowerCase(),
          );
        }
      }

      // Проверяем совпадение с publication_search_words
      let matchesSearchWords = false;
      if (searchWordsFromSettings.length > 0) {
        matchesSearchWords = searchWordsFromSettings.some((word) => word.includes(searchTerm));
      }

      // Если нет совпадений в publication_search_words, ищем по основным полям публикации
      let matchesMainFields = false;
      if (!matchesSearchWords) {
        const title = publication.title ? publication.title.toLowerCase() : '';
        const description = publication.description ? publication.description.toLowerCase() : '';
        const content = publication.content ? publication.content.toLowerCase() : '';

        // Для названия ищем по отдельным словам
        let matchesTitle = false;
        if (title) {
          const titleWords = title.split(/\s+/); // Разделяем по пробелам
          matchesTitle = titleWords.some((word) => word.includes(searchTerm));
        }

        // Для описания и контента используем обычный поиск
        matchesMainFields =
          matchesTitle || description.includes(searchTerm) || content.includes(searchTerm);
      }

      // Итоговый результат поиска
      matchesSearch = matchesSearchWords || matchesMainFields;
    }

    return matchesFilterA && matchesFilterB && matchesSearch;
  });

  return result;
};

const initialState = {
  currentTab: {},
  currentTabName: '',
  type: '',
  publications: [],
  blank_data: null,
  blank_name: '',
  blank_template: '',
  show_name: '',
  publication_filter_a: ['все'],
  publication_filter_b: ['все'],
  publication_search_words: [],
  filter_a_value: 'все',
  filter_b_value: 'все',
  search_value: '',
  filtered_publications: [],
  original_publications: [], // Оригинальные публикации для фильтрации
  skipFilterReset: false, // Флаг для пропуска сброса фильтров
  selected_post_filter: null, // Фильтр для показа только конкретного поста
};

const currentTabSlice = createSlice({
  name: 'currentTab',
  initialState,
  reducers: {
    setCurrentTab: (state, action) => {
      state.currentTab = action.payload;
      if (action.payload && action.payload.type) {
        state.type = action.payload.type;

        if (action.payload.type === 'tab_list' && action.payload.publications) {
          state.publications = action.payload.publications;

          // Сохраняем текущие значения фильтров, если установлен флаг пропуска сброса
          const currentFilterA = state.skipFilterReset ? state.publication_filter_a : ['все'];
          const currentFilterB = state.skipFilterReset ? state.publication_filter_b : ['все'];
          const currentSearchWords = state.skipFilterReset ? state.publication_search_words : [];
          const currentFilterAValue = state.skipFilterReset ? state.filter_a_value : 'все';
          const currentFilterBValue = state.skipFilterReset ? state.filter_b_value : 'все';
          const currentSearchValue = state.skipFilterReset ? state.search_value : '';

          // Сбрасываем массивы перед обработкой
          state.publication_filter_a = currentFilterA;
          state.publication_filter_b = currentFilterB;
          state.publication_search_words = currentSearchWords;

          // Восстанавливаем сохраненные значения фильтров, если установлен флаг пропуска сброса
          if (state.skipFilterReset) {
            state.filter_a_value = currentFilterAValue;
            state.filter_b_value = currentFilterBValue;
            state.search_value = currentSearchValue;
          }

          // Обрабатываем publication_settings для каждой публикации
          action.payload.publications.forEach((el, index) => {
            if (el.publication_settings) {
              // Проверяем, является ли строка валидным JSON
              const isJsonString =
                el.publication_settings.trim().startsWith('{') ||
                el.publication_settings.trim().startsWith('[');

              if (isJsonString) {
                try {
                  const obj = JSON.parse(el.publication_settings);

                  // Собираем все значения в массивы
                  if (obj.publication_filter_a) {
                    const filterAValues = Array.isArray(obj.publication_filter_a)
                      ? obj.publication_filter_a
                      : [obj.publication_filter_a];
                    const uniqueFilterA = getUniqueValues([
                      ...state.publication_filter_a,
                      ...filterAValues,
                    ]);
                    state.publication_filter_a = [
                      'все',
                      ...uniqueFilterA.filter((item) => item !== 'все'),
                    ];
                  }

                  if (obj.publication_filter_b) {
                    const filterBValues = Array.isArray(obj.publication_filter_b)
                      ? obj.publication_filter_b
                      : [obj.publication_filter_b];
                    const uniqueFilterB = getUniqueValues([
                      ...state.publication_filter_b,
                      ...filterBValues,
                    ]);
                    state.publication_filter_b = [
                      'все',
                      ...uniqueFilterB.filter((item) => item !== 'все'),
                    ];
                  }

                  if (obj.publication_search_words) {
                    const searchWords = obj.publication_search_words
                      .split(',')
                      .map((word) => word.trim());
                    state.publication_search_words = getUniqueValues([
                      ...state.publication_search_words,
                      ...searchWords,
                    ]);
                  }
                } catch (error) {
                  //   `Error parsing publication_settings JSON for publication ${index}:`,
                  //   error,
                  // );
                }
              } else {
                //   'publication_settings is not JSON, raw value:',
                //   el.publication_settings,
                // );
              }
            }
          });

          // Выводим финальные состояния после обработки всех публикаций
        } else {
          state.publications = [];
        }

        if (action.payload.type === 'tab_blank' && action.payload.content) {
          try {
            state.blank_data = JSON.parse(action.payload.content);
          } catch (error) {
            state.blank_data = [];
          }

          if (action.payload.template) {
            try {
              const template = JSON.parse(action.payload.template);
              state.blank_template = template.template_name;
            } catch (error) {
              state.blank_template = '';
            }
          }
          if (action.payload.blank_name) {
            state.blank_name = action.payload.blank_name;
          }
        } else {
          state.blank_data = [];
          state.blank_template = '';
        }
      }
      if (action.payload && action.payload.show_name) {
        state.show_name = action.payload.show_name;
      }
      if (action.payload && action.payload.eng_name) {
        state.currentTabName = action.payload.eng_name;
      }

      // Сохраняем оригинальные публикации для фильтрации
      state.original_publications = [...state.publications];
      //   'setCurrentTab - original_publications saved:',
      //   state.original_publications.length,
      // );

      // Обновляем отфильтрованные публикации после загрузки новых данных
      state.filtered_publications = filterPublications(
        state.original_publications,
        state.filter_a_value,
        state.filter_b_value,
        state.search_value,
        state.selected_post_filter,
      );

      // После фильтрации заменяем publications на filtered_publications
      state.publications = state.filtered_publications;
    },
    updateCurrentTab: (state, action) => {
      state.currentTab = { ...state.currentTab, ...action.payload };
    },
    clearCurrentTab: (state) => {
      state.currentTab = {};
    },
    setCurrentTabName: (state, action) => {
      state.currentTabName = action.payload;
    },
    clearCurrentTabName: (state) => {
      state.currentTabName = '';
    },
    setType: (state, action) => {
      state.type = action.payload;
    },
    clearType: (state) => {
      state.type = '';
    },
    setPublications: (state, action) => {
      state.publications = action.payload;
    },
    addPublication: (state, action) => {
      state.publications.push(action.payload);
    },
    removePublication: (state, action) => {
      state.publications = state.publications.filter((pub) => pub.id !== action.payload);
    },
    clearPublications: (state) => {
      state.publications = [];
    },
    setPublicationFilterA: (state, action) => {
      state.publication_filter_a = action.payload;
    },
    setPublicationFilterB: (state, action) => {
      state.publication_filter_b = action.payload;
    },
    setPublicationSearchWords: (state, action) => {
      state.publication_search_words = action.payload;
    },
    clearPublicationFilters: (state) => {
      state.publication_filter_a = ['все'];
      state.publication_filter_b = ['все'];
      state.publication_search_words = [];
    },
    setFilterAValue: (state, action) => {
      state.filter_a_value = action.payload;
    },
    setFilterBValue: (state, action) => {
      state.filter_b_value = action.payload;
    },
    setSearchValue: (state, action) => {
      state.search_value = action.payload;
    },
    resetFilters: (state) => {
      state.filter_a_value = 'все';
      state.filter_b_value = 'все';
      state.search_value = '';
      state.selected_post_filter = null;
    },
    resetFiltersOnly: (state) => {
      state.filter_a_value = 'все';
      state.filter_b_value = 'все';
      state.selected_post_filter = null;
      // Не сбрасываем search_value
    },
    setSkipFilterReset: (state, action) => {
      state.skipFilterReset = action.payload;
    },
    updateFilteredPublications: (state) => {
      state.filtered_publications = filterPublications(
        state.original_publications,
        state.filter_a_value,
        state.filter_b_value,
        state.search_value,
        state.selected_post_filter,
      );

      // После фильтрации заменяем publications на filtered_publications
      state.publications = state.filtered_publications;
    },
    setSelectedPostFilter: (state, action) => {
      state.selected_post_filter = action.payload;
    },
    clearSelectedPostFilter: (state) => {
      state.selected_post_filter = null;
    },
  },
});

export const {
  setCurrentTab,
  updateCurrentTab,
  clearCurrentTab,
  setCurrentTabName,
  clearCurrentTabName,
  setType,
  clearType,
  setPublications,
  addPublication,
  removePublication,
  clearPublications,
  setPublicationFilterA,
  setPublicationFilterB,
  setPublicationSearchWords,
  clearPublicationFilters,
  setFilterAValue,
  setFilterBValue,
  setSearchValue,
  resetFilters,
  resetFiltersOnly,
  setSkipFilterReset,
  updateFilteredPublications,
  setSelectedPostFilter,
  clearSelectedPostFilter,
} = currentTabSlice.actions;

export default currentTabSlice.reducer;
